import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiUrl';

function fmtFecha(s?: string | null) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface BloqueSnapshot {
  bloque_id: string;
  num: number | string;
  ancla_anterior: string | null;
  estadio_anterior?: string | null;
  estadio_anterior_label?: string | null;
}

export function AnclaSolicitudPage({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [comentario, setComentario] = useState('');

  const solicitud = data?.solicitud;
  const lote = data?.lote;
  const resuelta = solicitud && solicitud.estado !== 'pendiente';
  const vencida = data?.expired;
  const bloques: BloqueSnapshot[] = solicitud?.bloques ?? [];
  const columnaLabel = String(solicitud?.ancla_columna || '').replace('fecha_', '');
  const esRetroceso = solicitud?.tipo === 'retroceso_estado';
  const origenesUnicos = Array.from(new Set(bloques.map(b => b.estadio_anterior_label || b.estadio_anterior).filter(Boolean)));

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(buildApiUrl(`/aplicaciones/estadios/ancla-solicitudes/${token}`))
      .then(async (resp) => {
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(payload?.error || 'No se pudo cargar la solicitud');
        return payload;
      })
      .then((payload) => { if (active) setData(payload); })
      .catch((err) => active && setError(err?.message || 'No se pudo cargar la solicitud'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const estadoTexto = useMemo(() => {
    if (!solicitud) return '';
    if (vencida && solicitud.estado === 'pendiente') return 'Vencida';
    if (solicitud.estado === 'aprobada') return 'Aprobada';
    if (solicitud.estado === 'rechazada') return 'Rechazada';
    if (solicitud.estado === 'cancelada') return 'Cancelada';
    return 'Pendiente';
  }, [solicitud, vencida]);

  async function resolver(decision: 'aprobada' | 'rechazada') {
    setSaving(true);
    setError('');
    try {
      const resp = await fetch(buildApiUrl(`/aplicaciones/estadios/ancla-solicitudes/${token}/resolver`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, comentario }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || 'No se pudo resolver');
      setData((prev: any) => ({ ...prev, solicitud: payload.solicitud, expired: false }));
    } catch (err: any) {
      setError(err?.message || 'No se pudo resolver');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen px-4 py-5" style={{ background: '#07111f', color: '#f8fafc' }}>
      <div className="mx-auto max-w-md">
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#93c5fd' }}>Control de Piña</div>
          <h1 className="mt-1 text-xl font-bold">{esRetroceso ? 'Retroceso de estado' : 'Corrección de fecha-ancla'}</h1>
        </div>

        {loading && (
          <div className="flex items-center gap-2 rounded-lg border p-4" style={{ borderColor: '#1f2f46', background: '#0f1d31' }}>
            <Loader2 size={18} className="animate-spin" /> Cargando solicitud...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border p-4 text-sm" style={{ borderColor: '#7f1d1d', background: '#35131a', color: '#fecaca' }}>{error}</div>
        )}

        {!loading && solicitud && (
          <div className="rounded-lg border p-4" style={{ borderColor: '#1f2f46', background: '#0f1d31' }}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs" style={{ color: '#93a4bb' }}>Lote</div>
                <div className="text-2xl font-extrabold">
                  {lote?.numero ? `Lote ${lote.numero}` : `#${solicitud.lote_id ?? ''}`}
                </div>
                <div className="text-sm" style={{ color: '#93a4bb' }}>
                  {esRetroceso
                    ? <>{bloques.length} bloque{bloques.length === 1 ? '' : 's'} · <strong>{origenesUnicos.join(', ') || '—'}</strong> → <strong style={{ color: '#93c5fd' }}>{solicitud.estadio_destino_label || solicitud.estadio_destino}</strong></>
                    : <>{bloques.length} bloque{bloques.length === 1 ? '' : 's'} · campo <strong>{columnaLabel}</strong></>}
                </div>
              </div>
              <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: resuelta ? '#123a2a' : '#3b2800', color: resuelta ? '#86efac' : '#fbbf24' }}>
                {estadoTexto}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border p-3" style={{ borderColor: '#1f2f46', background: '#081222' }}>
                <div className="text-xs" style={{ color: '#93a4bb' }}>{esRetroceso ? 'Fecha del cambio' : 'Fecha propuesta'}</div>
                <div className="mt-1 text-xl font-black" style={{ color: '#93c5fd' }}>{fmtFecha(solicitud.fecha_nueva)}</div>
              </div>
              <div className="rounded-md border p-3" style={{ borderColor: '#854d0e', background: '#211704' }}>
                <div className="text-xs" style={{ color: '#facc15' }}>Solicitado por</div>
                <div className="mt-1 text-sm font-bold" style={{ color: '#fbbf24' }}>{solicitud.solicitado_por || '—'}</div>
              </div>
            </div>

            <div className="mt-4 rounded-md border p-3 text-sm" style={{ borderColor: '#1f2f46', background: '#081222', color: '#cbd5e1' }}>
              <div className="font-bold" style={{ color: '#f8fafc' }}>Motivo</div>
              {solicitud.motivo || 'Sin detalle adicional.'}
            </div>

            <div className="mt-4 rounded-md border" style={{ borderColor: '#1f2f46', background: '#081222' }}>
              <div className="px-3 py-2 text-xs font-bold uppercase tracking-wide" style={{ color: '#93a4bb', borderBottom: '1px solid #1f2f46' }}>
                Bloques afectados
              </div>
              <div className="max-h-48 overflow-y-auto divide-y" style={{ borderColor: '#1f2f46' }}>
                {bloques.map((b) => (
                  <div key={b.bloque_id} className="flex items-center justify-between px-3 py-2 text-xs" style={{ borderColor: '#1f2f46' }}>
                    <span className="font-bold">BLK {b.num}</span>
                    <span style={{ color: '#93a4bb' }}>
                      {esRetroceso
                        ? <>{b.estadio_anterior_label || b.estadio_anterior || '—'} → <strong style={{ color: '#93c5fd' }}>{solicitud.estadio_destino_label || solicitud.estadio_destino}</strong></>
                        : <>{fmtFecha(b.ancla_anterior)} → <strong style={{ color: '#93c5fd' }}>{fmtFecha(solicitud.fecha_nueva)}</strong></>}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {!resuelta && !vencida && (
              <div className="mt-4 space-y-3">
                <textarea
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Comentario opcional"
                  className="min-h-20 w-full rounded-md border px-3 py-2 text-sm outline-none"
                  style={{ borderColor: '#1f2f46', background: '#07111f', color: '#f8fafc' }}
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    disabled={saving}
                    onClick={() => resolver('aprobada')}
                    className="flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-bold disabled:opacity-50"
                    style={{ background: '#14532d', color: '#bbf7d0' }}
                  >
                    <CheckCircle2 size={16} /> Aprobar
                  </button>
                  <button
                    disabled={saving}
                    onClick={() => resolver('rechazada')}
                    className="flex items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-bold disabled:opacity-50"
                    style={{ background: '#3f1218', color: '#fecaca' }}
                  >
                    <XCircle size={16} /> Rechazar
                  </button>
                </div>
              </div>
            )}

            {resuelta && (
              <div className="mt-4 rounded-md border p-3 text-sm" style={{ borderColor: '#1f2f46', background: '#081222', color: '#cbd5e1' }}>
                {solicitud.estado === 'aprobada'
                  ? (esRetroceso ? 'Aprobada: el estado ya quedó actualizado en el sistema.' : 'Aprobada: la fecha ya quedó corregida en el sistema.')
                  : solicitud.estado === 'rechazada'
                    ? 'Rechazada: no se hizo ningún cambio.'
                    : 'Esta solicitud ya no está pendiente.'}
                {solicitud.respuesta_comentario && (
                  <div className="mt-2 italic" style={{ color: '#93a4bb' }}>"{solicitud.respuesta_comentario}"</div>
                )}
              </div>
            )}

            {!resuelta && vencida && (
              <div className="mt-4 rounded-md border p-3 text-sm" style={{ borderColor: '#7f1d1d', background: '#2a0c0c', color: '#fecaca' }}>
                Esta solicitud venció. Pedile a quien la envió que genere una nueva.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
